import type { Components } from 'react-markdown';
import type z from 'zod';
import { createColumnHelper } from '@tanstack/react-table';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { EcosystemRecordSchema } from '@ballerine/common';
import { checkIsDomainOrUrl } from '@ballerine/common';

import { BallerineLink } from '@/components/atoms/BallerineLink';
import { TextWithNAFallback } from '@/components/atoms/TextWithNAFallback';

const markdownComponents: Components = {
  p: ({ node, ...props }) => <p className="my-3 text-justify text-sm text-slate-800" {...props} />,
  h2: ({ node, ...props }) => (
    <h2 className="mb-1 mt-2 text-lg font-semibold text-slate-800" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="mb-1 mt-2 text-base font-semibold text-slate-800" {...props} />
  ),
  ul: ({ node, ...props }) => <ul className="my-3 list-disc space-y-2 pl-6" {...props} />,
  ol: ({ node, ...props }) => <ol className="my-3 list-decimal space-y-2 pl-6" {...props} />,
  li: ({ node, ...props }) => <li className="pl-1.5 text-sm" {...props} />,
  a: ({ node, ...props }) => (
    <a
      className="mx-1 inline-block rounded-full bg-indigo-50 px-2 py-0 text-[0.7rem] text-neutral-400 hover:underline"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="my-3 border-l-2 pl-4 italic"
      style={{ borderColor: '#EFF2FF', color: '#14203D' }}
      {...props}
    />
  ),
  hr: ({ node, ...props }) => (
    <div className="my-5 w-full">
      <hr
        className="relative -mx-6"
        style={{ borderColor: '#EFF2FF', width: 'calc(100% + 3rem)' }}
        {...props}
      />
      <div
        className="relative -mx-6 h-4"
        style={{
          width: 'calc(100% + 3rem)',
          background: 'linear-gradient(0deg, #FFFFFF 0%, rgba(244, 246, 253, 0.4) 100%)',
        }}
      />
    </div>
  ),
  table: ({ node, ...props }) => (
    <table className="my-2 w-full border-collapse text-xs" {...props} />
  ),
  thead: ({ node, ...props }) => <thead className="bg-slate-50" {...props} />,
  tbody: ({ node, ...props }) => <tbody {...props} />,
  tr: ({ node, ...props }) => <tr className="border-b last:border-b-0" {...props} />,
  th: ({ node, ...props }) => (
    <th className="border border-slate-200 px-2 py-1 text-left font-semibold" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="border border-slate-200 px-2 py-1 align-top" {...props} />
  ),
};

type EcosystemColumnType = Extract<z.infer<typeof EcosystemRecordSchema>, { nodes: any }>;

const columnHelper = createColumnHelper<EcosystemColumnType>();

const addProtocolIfMissing = (url: string) => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return `http://${url}`;
};

export const columns = [
  columnHelper.accessor('domain', {
    header: 'Domain/Entity',
    cell: info => {
      const domainOrEntity = info.getValue();

      if (checkIsDomainOrUrl(domainOrEntity)) {
        const domainWithProtocol = addProtocolIfMissing(domainOrEntity);

        return (
          <div className="min-w-0 max-w-[130px] break-words">
            <BallerineLink
              href={domainWithProtocol}
              title={domainWithProtocol}
              className="block justify-start break-words"
            >
              {domainOrEntity}
            </BallerineLink>
          </div>
        );
      }

      return (
        <div className="min-w-0 max-w-[130px] break-words">
          <TextWithNAFallback className="block break-words" title={domainOrEntity}>
            {domainOrEntity}
          </TextWithNAFallback>
        </div>
      );
    },
    size: 300,
  }),
  columnHelper.accessor('relationship', {
    header: 'Relationship Type',
    cell: info => {
      const relationship = info.getValue();

      return (
        <div className="min-w-0 max-w-[130px] break-words">
          <TextWithNAFallback className="block break-words" title={relationship}>
            {relationship}
          </TextWithNAFallback>
        </div>
      );
    },
  }),
  columnHelper.accessor('nodes', {
    header: 'Connected By',
    cell: info => {
      const nodes = info.getValue();
      const markdown =
        nodes?.map(node => `- **${node.type}**\n    ${node.value}`)?.join('\n') ?? '';

      if (!markdown) {
        return <TextWithNAFallback className="block" />;
      }

      return (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {markdown}
        </ReactMarkdown>
      );
    },
  }),
];
